<template>
  <layout-basic>
    <page-hero
      title="Webinars"
      lead="Join a free IMPROVE Live webinar: a short live demonstration of IMPROVE on Microsoft Teams, with time for your questions."
    />

    <section class="webinars">
      <v-container>
        <div class="webinars__body">
          <h2 class="font-sora webinars__title">Upcoming live webinars</h2>
          <p class="webinars__lead">
            Choose a date to book your place. There is no account to create, and
            we email you the calendar invite and the Teams link.
          </p>

          <div v-if="loading" class="webinars__centre">
            <v-progress-circular indeterminate color="#08a0a8" />
          </div>
          <ul v-else-if="upcoming.length" class="webinars__list">
            <li v-for="s in upcoming" :key="s.session_at" class="webinars__item">
              <div>
                <p class="webinars__date">{{ longDate(s.label) }}</p>
                <p class="webinars__time">
                  {{ s.time }} to {{ endTime(s.time) }} UK time,
                  <span :class="{ 'is-full': s.is_full }">{{ placesText(s) }}</span>
                </p>
              </div>
              <router-link
                v-if="!s.is_full"
                :to="{ path: '/improve-live', query: { date: s.date }, hash: '#book' }"
                class="webinars__book"
              >
                Book
              </router-link>
            </li>
          </ul>
          <p v-else class="webinars__none">
            There are no webinar dates open right now.
            <router-link to="/contact">Contact us</router-link> and we will arrange a session.
          </p>

          <p class="webinars__note">
            On-demand recordings will appear here. Until then, watch the product
            demo or experience IMPROVE for yourself.
          </p>
        </div>
      </v-container>
    </section>

    <point-grid :points="points" :columns="2" tint />
    <standard-cta />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import ImproveLiveAPI from '@/Api/improveLive.js';
import { marketingPageMeta } from '@/utils/marketingPage';

// How many upcoming webinars the list shows (the IMPROVE Live page's
// calendar has them all).
const SHOWN = 6;

export default {
  name: 'Webinars',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    StandardCta,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'Webinars | IMPROVE',
      description: 'Book a free IMPROVE Live webinar: a short live demonstration of IMPROVE on Microsoft Teams, with time for your questions.',
      path: '/resources/webinars',
    });
  },
  data() {
    return {
      loading: true,
      durationMinutes: 20,
      sessions: [],
      points: [
        'Employee onboarding that scales',
        'Compliance without the complexity',
        'Building better managers',
        'Getting the most from IMPROVE',
      ],
    };
  },
  computed: {
    upcoming() {
      return this.sessions.slice(0, SHOWN);
    },
  },
  mounted() {
    // Prerendered pages must not bake in dates that will go stale.
    if (typeof window !== 'undefined' && window.__PRERENDER_HOST__) return;
    ImproveLiveAPI.sessions()
      .then((data) => {
        this.durationMinutes = Number(data.duration_minutes) || 20;
        this.sessions = data.enabled && Array.isArray(data.sessions) ? data.sessions : [];
      })
      .catch(() => {
        this.sessions = [];
      })
      .then(() => {
        this.loading = false;
      });
  },
  methods: {
    longDate(label) {
      return String(label || '').split(', ')[0];
    },
    endTime(time) {
      const parts = String(time || '0:0').split(':');
      const total = Number(parts[0]) * 60 + Number(parts[1]) + this.durationMinutes;
      const pad = (n) => (n < 10 ? '0' : '') + n;
      return pad(Math.floor(total / 60) % 24) + ':' + pad(total % 60);
    },
    placesText(s) {
      if (s.is_full) return 'fully booked';
      return s.seats_remaining + (s.seats_remaining === 1 ? ' place left' : ' places left');
    },
  },
};
</script>

<style scoped>
.webinars {
  padding: 8px 0 32px;
}

/* The width lives on an inner block: the site forces .container to 1420px !important. */
.webinars__body {
  max-width: 760px;
  margin: 0 auto;
}

/* The site sets every a and span to 16px; these follow their line. */
.webinars__time span,
.webinars__none a {
  font-size: inherit;
}

.webinars__title {
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: 700;
  color: var(--brand-navy, #002458);
  text-align: center;
}

.webinars__lead {
  max-width: 560px;
  margin: 0 auto 20px;
  text-align: center;
  color: var(--brand-grey, #6b7280);
  font-size: 15px;
  line-height: 1.5;
}

.webinars__centre {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

.webinars__list {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid #e3e9f1;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
}

.webinars__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
}

.webinars__item + .webinars__item {
  border-top: 1px solid #eef1f5;
}

.webinars__date {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--brand-navy, #002458);
}

.webinars__time {
  margin: 2px 0 0;
  font-size: 14px;
  color: var(--brand-grey, #6b7280);
}

.webinars__time .is-full {
  color: #b3261e;
}

.webinars__book {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  background: var(--brand-navy, #002458);
  color: #fff !important;
  font-weight: 700;
  font-size: 14px;
  text-decoration: none;
  white-space: nowrap;
}

.webinars__book:hover {
  background: var(--brand-teal, #08a0a8);
}

.webinars__none {
  text-align: center;
  color: var(--brand-navy, #002458);
  font-size: 15px;
}

.webinars__note {
  max-width: 560px;
  margin: 20px auto 0;
  text-align: center;
  color: var(--brand-grey, #6b7280);
  font-size: 14px;
  line-height: 1.5;
}
</style>
